import React, { useState, useEffect } from 'react'; // Removed useCallback
import apiService from '../../services/api'; // Assuming api service is correctly set up

const GenreFilterModal = ({ isOpen, onClose, onApply, initialSelectedGenres = [] }) => {
  const [allGenres, setAllGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]); // Store objects { genreId, genre }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(''); // For filtering the genre list

  // Fetch all genres when the modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchGenres = async () => {
        setIsLoading(true);
        setError(null);
        try {
          // Assuming an endpoint /genres/all exists in apiService
          // If not, we need to add it to api.js
          const response = await apiService.getAllGenres(); // Needs implementation in api.js
          setAllGenres(response || []);
        } catch (err) {
          console.error("Error fetching genres:", err);
          setError("Failed to load genres.");
          setAllGenres([]); // Clear genres on error
        } finally {
          setIsLoading(false);
        }
      };
      fetchGenres();
    } else {
      // Reset state when modal closes
      setSearchTerm('');
      setError(null);
      // Don't reset allGenres, maybe cache it? For now, it refetches.
    }
  }, [isOpen]);

  // Initialize selected genres when modal opens based on initial prop
  useEffect(() => {
    if (isOpen) {
      // Find the full genre objects based on the initial IDs
      const initialFullGenres = allGenres.filter(genre =>
        initialSelectedGenres.includes(genre.genreId)
      );
      setSelectedGenres(initialFullGenres);
    }
  }, [isOpen, initialSelectedGenres, allGenres]);


  const handleCheckboxChange = (event, genre) => {
    if (event.target.checked) {
      setSelectedGenres(prev => [...prev, genre]);
    } else {
      setSelectedGenres(prev => prev.filter(g => g.genreId !== genre.genreId));
    }
  };

  const handleRemoveGenre = (genreId) => {
    setSelectedGenres(prev => prev.filter(g => g.genreId !== genreId));
  };

  const handleApply = () => {
    const selectedIds = selectedGenres.map(g => g.genreId);
    onApply(selectedIds); // Pass back only the IDs
    onClose();
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  // Filter genres based on search term
  const filteredGenres = allGenres.filter(genre =>
    genre.genre.toLowerCase().includes(searchTerm)
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center p-4">
      {/* Increased max-width from 2xl to 4xl */}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Filter by Genre</h2>
        </div>

        {/* Selected Genres Display */}
        {selectedGenres.length > 0 && (
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <span className="text-sm font-medium text-gray-600 mr-2">Selected:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {selectedGenres.map(genre => (
                <span key={genre.genreId} className="flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                  {genre.genre}
                  <button
                    onClick={() => handleRemoveGenre(genre.genreId)}
                    className="ml-1.5 text-blue-600 hover:text-blue-800 focus:outline-none"
                    aria-label={`Remove ${genre.genre}`}
                  >
                    &times; {/* Multiplication sign for 'X' */}
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Search Input */}
        <div className="p-3 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search genres..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* Genre List */}
        <div className="p-4 overflow-y-auto flex-grow">
          {isLoading && <p className="text-gray-500 text-center">Loading genres...</p>}
          {error && <p className="text-red-500 text-center">{error}</p>}
          {!isLoading && !error && (
            // Apply grid layout for 5 columns, setting horizontal gap back to gap-x-8
            <ul className="grid grid-cols-5 gap-x-8 gap-y-2">
              {filteredGenres.length > 0 ? (
                filteredGenres.map(genre => (
                  // Changed items-center to items-start for better wrapped text alignment
                  <li key={genre.genreId} className="flex items-start text-sm">
                    <input
                      type="checkbox"
                      id={`genre-${genre.genreId}`}
                      checked={selectedGenres.some(g => g.genreId === genre.genreId)}
                      onChange={(e) => handleCheckboxChange(e, genre)}
                      // Added mt-1 to slightly adjust checkbox position relative to text
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2 mt-1 flex-shrink-0" // Added flex-shrink-0
                    />
                    {/* Removed ID display from label and added text-left */}
                    <label htmlFor={`genre-${genre.genreId}`} className="text-sm text-gray-700 cursor-pointer text-left">
                      {genre.genre}
                    </label>
                  </li>
                ))
              ) : (
                // Span across all 5 columns if no results
                <p className="text-gray-500 text-center text-sm col-span-5">No genres found matching "{searchTerm}".</p>
              )}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end space-x-2 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenreFilterModal;
